import { createAsyncAction, createAction } from 'typesafe-actions';
import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import * as firebase from 'firebase/app'
import { webApiPost, webApiGet, RESET_PASSWORD_REDIRECT_URL } from '../../api/client';
import { AbnLookupResult } from '../../api/models/AbnLookupResult';
import { Business } from '../../api/models/BusinessData';
import { Restaurant } from '../../api/models/RestaurantData';
import { Supplier } from '../../api/models/QuotedData';
import { browserHistory } from '../../store/store';
import { Credentials } from '../../api/models/Credentials';

export type LoginData = {
    tokenResult: firebase.auth.IdTokenResult,
    displayName: string | null
}

export const loginAsync = createAsyncAction(
    "LOGIN_REQUEST",
    "LOGIN_SUCCESS",
    "LOGIN_ERROR")<undefined, LoginData, Error>()


export const loginAsyncThunk = (email: string, password: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(loginAsync.request())

            firebase.auth().signInWithEmailAndPassword(email, password)
                .then((result: firebase.auth.UserCredential) => {
                    result!.user!.getIdTokenResult().then((tokenResult: firebase.auth.IdTokenResult) => {
                        dispatch(loginAsync.success({ tokenResult, displayName: result!.user!.displayName }))
                        browserHistory.push('/')
                        resolve()
                    }).catch((ex: any) => {
                        dispatch(loginAsync.failure(ex))
                    })
                }).catch((ex: any) => {
                    dispatch(loginAsync.failure(ex))
                    resolve()
                })
        })
    }
}


export const logoutAsync = createAsyncAction(
    "LOGOUT_REQUEST",
    "LOGOUT_SUCCESS",
    "LOGOUT_ERROR")<void, void, Error>()


export const logoutAsyncThunk = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(logoutAsync.request())

            firebase.auth().signOut()
                .then(() => {
                    dispatch(logoutAsync.success())
                    resolve()
                }).catch((ex: any) => {
                    dispatch(logoutAsync.failure(ex))
                    resolve()
                })
        })
    }
}


export const registerRestaurantAsync = createAsyncAction(
    "REGISTER_RESTAURANT_REQUEST",
    "REGISTER_RESTAURANT_SUCCESS",
    "REGISTER_RESTAURANT_ERROR")<undefined, string, Error>()


export const registerRestaurantAsyncThunk = (business: Business, restaurant: Restaurant, credentials: Credentials): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(registerRestaurantAsync.request())

            webApiPost("/auth/restaurant",
                { business: business, restaurant: restaurant, credentials },
                (data: any) => {
                    firebase.auth().signInWithCustomToken(data.token).then(() => {
                        dispatch(registerRestaurantAsync.success(data.token)); resolve()
                    })
                },
                (err: Error) => { dispatch(registerRestaurantAsync.failure(err)); resolve() })
        })
    }
}

export const registerSupplierAsync = createAsyncAction(
    "REGISTER_SUPPLIER_REQUEST",
    "REGISTER_SUPPLIER_SUCCESS",
    "REGISTER_SUPPLIER_ERROR")<undefined, string, Error>()


export const registerSupplierAsyncThunk = (business: Business, supplier: Supplier, credentials: Credentials): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(registerSupplierAsync.request())

            webApiPost("/auth/supplier",
                { business: business, supplier: supplier, credentials },
                (data: any) => {
                    firebase.auth().signInWithCustomToken(data.token).then(() => {
                        dispatch(registerSupplierAsync.success(data.token)); resolve()
                    })
                },
                (err: Error) => { dispatch(registerSupplierAsync.failure(err)); resolve() })
        })
    }
}


export const resetPasswordEmailAsync = createAsyncAction(
    "SEND_RESET_PASSWORD_EMAIL_REQUEST",
    "SEND_RESET_PASSWORD_EMAIL_SUCCESS",
    "SEND_RESET_PASSWORD_EMAIL_ERROR")<void, void, Error>()


export const resetPasswordEmailAsyncThunk = (email: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(resetPasswordEmailAsync.request())

            var actionCodeSettings = {
                url: RESET_PASSWORD_REDIRECT_URL,
                handleCodeInApp: false
            };

            firebase.auth().sendPasswordResetEmail(email, actionCodeSettings).then(
                () => {
                    dispatch(resetPasswordEmailAsync.success());
                    resolve();
                }).catch((ex: any) => {
                    dispatch(resetPasswordEmailAsync.failure(ex))
                    resolve()
                })
        })
    }
}

export const resetPasswordConfirmAsync = createAsyncAction(
    "RESET_PASSWORD_CONFIRM_REQUEST",
    "RESET_PASSWORD_CONFIRM_SUCCESS",
    "RESET_PASSWORD_CONFIRM_ERROR")<void, void, Error>()


export const resetPasswordConfirmAsyncThunk = (code: string, newPassword: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(resetPasswordConfirmAsync.request())

            firebase.auth().confirmPasswordReset(code, newPassword)
                .then(() => {
                    dispatch(resetPasswordConfirmAsync.success())
                    resolve();
                })
                .catch((ex: any) => {
                    dispatch(resetPasswordConfirmAsync.failure(ex))
                    resolve();
                })
        })
    }
}



export const refreshTokenAsync = createAsyncAction(
    "REFRESH_TOKEN",// No _REQUEST suffix on purpose - don't change.
    "REFRESH_TOKEN_SUCCESS",
    "REFRESH_TOKEN_ERROR")<undefined, LoginData, Error>()



export const refreshTokenAsyncThunk = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(refreshTokenAsync.request())

            if (firebase.auth().currentUser === null) {
                //dispatch(refreshTokenAsync.failure({ message: 'No current user, to refresh token for.', name: "", stack: "" }))
                console.log('No current user, to refresh token for.')
                resolve()
            }

            firebase.auth().currentUser!.getIdTokenResult(true)
                .then((result: firebase.auth.IdTokenResult) => {
                    dispatch(refreshTokenAsync.success({ tokenResult: result, displayName: firebase.auth().currentUser!.displayName }))
                    resolve()
                }).catch((ex: any) => {
                    console.log("Can't refresh token!")
                    debugger;
                    dispatch(refreshTokenAsync.failure(ex))
                    resolve()
                })
        })
    }
}

export const abnLookupAsync = createAsyncAction(
    "ABN_LOOKUP_REQUEST",
    "ABN_LOOKUP_SUCCESS",
    "ABN_LOOKUP_ERROR")<undefined, AbnLookupResult, Error>()


export const abnLookupAsyncThunk = (abn: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        return new Promise<void>((resolve) => {
            dispatch(abnLookupAsync.request())

            webApiGet(
                "/auth/checkabn",
                (data: AbnLookupResult) => {
                    dispatch(abnLookupAsync.success(data));
                    resolve()
                },
                (err: Error) => { dispatch(abnLookupAsync.failure(err)); resolve() },
                { abn },
                3
            )
        })
    }
}


export const updateSignUpData = createAction("UPDATE_SIGN_UP_DETAILS")<Supplier | Restaurant>()

export const resetSignUpForm = createAction("RESET_SIGNUP_FORM")()
export const updateUserAutheticatedInfo = createAction("UPDATE_USER_AUTH_STATUS")<boolean>()